/**
 * Störungen — Auswertung im Leitstand-Stil (Phase B, Befund-A-Regeln):
 * Langläufer über 12 h (vergessene Beenden-Klicks) verfälschen keine Summen
 * mehr, sondern stehen einzeln benannt; Teststörungen zählen nirgends mit.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kennzahl,
  Kopfzelle,
  Notiz,
  Rangliste,
  Tabelle,
  Zelle,
} from '../components/leitstand';

interface Zeile {
  name: string;
  anzahl: number;
  minuten: number;
}
interface Langlaeufer {
  id: string;
  beginn: string;
  ort: string;
  kategorie: string;
  dauerH: number;
  offen: boolean;
}
interface Daten {
  gesamt: {
    anzahl: number;
    offen: number;
    nachtraege: number;
    stillstandMin: number;
    langlaeufer: number;
  };
  kategorien: Zeile[];
  orte: Zeile[];
  langlaeufer: Langlaeufer[];
}

type Preset = 'woche' | 'monat' | 'quartal' | 'jahr' | 'frei';

function tagPlus(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function zeitraumFuer(preset: Preset): { von: string; bis: string } {
  const heute = getTodayBerlin(new Date());
  if (preset === 'woche') {
    const wochentag = new Date(`${heute}T00:00:00Z`).getUTCDay();
    return { von: tagPlus(heute, -((wochentag + 6) % 7)), bis: heute };
  }
  if (preset === 'monat') return { von: `${heute.slice(0, 8)}01`, bis: heute };
  if (preset === 'jahr') return { von: `${heute.slice(0, 4)}-01-01`, bis: heute };
  return { von: tagPlus(heute, -89), bis: heute };
}

const PRESETS: Array<{ schluessel: Preset; label: string }> = [
  { schluessel: 'woche', label: 'Diese Woche' },
  { schluessel: 'monat', label: 'Dieser Monat' },
  { schluessel: 'quartal', label: '90 Tage' },
  { schluessel: 'jahr', label: 'Dieses Jahr' },
  { schluessel: 'frei', label: 'Zeitraum' },
];

function fmt(wert: number, nachkomma = 0): string {
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  });
}

function fmtDauerMin(min: number): string {
  if (min < 60) return `${fmt(min)} min`;
  return `${fmt(min / 60, 1)} h`;
}

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
function fmtBeginn(iso: string): string {
  const d = new Date(iso);
  const tag = d.toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
  });
  const zeit = d.toLocaleTimeString('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
  });
  const wd =
    WOCHENTAGE[new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Berlin' })).getDay()];
  return `${wd} ${tag} ${zeit}`;
}

export default function DisruptionsLeitstand() {
  const { user } = useAuth();

  const [preset, setPreset] = useState<Preset>('quartal');
  const [freiVon, setFreiVon] = useState('');
  const [freiBis, setFreiBis] = useState('');
  const [daten, setDaten] = useState<Daten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const zeitraum = useMemo(() => {
    if (preset === 'frei') return freiVon && freiBis ? { von: freiVon, bis: freiBis } : null;
    return zeitraumFuer(preset);
  }, [preset, freiVon, freiBis]);

  const lade = useCallback(async () => {
    if (!zeitraum) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(
        `/api/reports/disruptions/leitstand?from=${zeitraum.von}&to=${zeitraum.bis}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Die Auswertung konnte nicht geladen werden');
      }
      const body = await res.json();
      if (!body?.gesamt) throw new Error('Unerwartete Antwort vom Server');
      setDaten(body);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Auswertung konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [zeitraum]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const alsRang = (zeilen: Zeile[], max = 8) => {
    const top = zeilen.slice(0, max);
    const spitze = top[0]?.minuten ?? 0;
    return top.map((z) => ({
      text: z.name,
      wert: fmtDauerMin(z.minuten),
      neben: `${fmt(z.anzahl)} ×`,
      anteil: spitze > 0 ? Math.max(2, (z.minuten / spitze) * 100) : 0,
    }));
  };

  return (
    <LeitstandRahmen
      titel="Störungen"
      benutzer={user?.displayName}
      werkzeuge={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.schluessel}
                onClick={() => setPreset(p.schluessel)}
                aria-pressed={preset === p.schluessel}
                className={`rounded-[4px] border px-[11px] py-1.5 font-zahl text-xs transition-colors ${
                  preset === p.schluessel
                    ? 'border-akzent bg-akzent text-akzent-satz'
                    : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-linie-stark'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'frei' && (
            <span className="flex items-center gap-1.5">
              <input
                type="date"
                value={freiVon}
                onChange={(e) => setFreiVon(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
              <span className="text-xs text-tinte-3">bis</span>
              <input
                type="date"
                value={freiBis}
                onChange={(e) => setFreiBis(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
            </span>
          )}
        </div>
      }
    >
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !daten && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Auswertung …</p>
      )}

      {daten && (
        <div className={`flex flex-col gap-6 ${laden ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Störungen"
              wert={fmt(daten.gesamt.anzahl)}
              neben={
                daten.gesamt.nachtraege > 0
                  ? `davon ${fmt(daten.gesamt.nachtraege)} nachgetragen`
                  : undefined
              }
            />
            <Kennzahl
              marke="Stillstand"
              wert={fmt(daten.gesamt.stillstandMin / 60, 1)}
              einheit="h"
              neben="ohne mutmaßlich vergessene Enden"
            />
            <Kennzahl
              marke="Gerade offen"
              wert={fmt(daten.gesamt.offen)}
              tendenz={daten.gesamt.offen > 0 ? 'runter' : 'neutral'}
            />
            <Kennzahl
              marke="Vermutlich vergessene Enden"
              wert={fmt(daten.gesamt.langlaeufer)}
              neben="länger als 12 h — unten einzeln"
              tendenz={daten.gesamt.langlaeufer > 0 ? 'runter' : 'neutral'}
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Karte titel="Nach Kategorie" marke="Stillstand · nur glaubwürdige Dauern">
              {daten.kategorien.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Störungen im Zeitraum.</p>
              ) : (
                <Rangliste eintraege={alsRang(daten.kategorien)} />
              )}
            </Karte>
            <Karte titel="Nach Maschine / Ort" marke="Stillstand · nur glaubwürdige Dauern">
              {daten.orte.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Störungen im Zeitraum.</p>
              ) : (
                <Rangliste eintraege={alsRang(daten.orte)} />
              )}
            </Karte>
          </div>

          <Karte
            titel="Vermutlich vergessene Enden"
            marke="Dauer über 12 Stunden · zählt in keiner Summe"
            tabelle
          >
            {daten.langlaeufer.length === 0 ? (
              <p className="py-4 text-sm text-tinte-3">
                Keine — alle Störungen wurden zeitnah beendet.
              </p>
            ) : (
              <>
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Beginn</Kopfzelle>
                      <Kopfzelle>Maschine / Ort</Kopfzelle>
                      <Kopfzelle>Kategorie</Kopfzelle>
                      <Kopfzelle rechts>angebliche Dauer</Kopfzelle>
                      <Kopfzelle rechts>Zustand</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {daten.langlaeufer.slice(0, 20).map((l, i) => {
                      const letzte = i === Math.min(daten.langlaeufer.length, 20) - 1;
                      return (
                        <tr key={l.id}>
                          <Zelle zahl letzte={letzte}>
                            {fmtBeginn(l.beginn)}
                          </Zelle>
                          <Zelle letzte={letzte}>{l.ort}</Zelle>
                          <Zelle letzte={letzte}>{l.kategorie}</Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            {fmt(l.dauerH, 1)} h
                          </Zelle>
                          <Zelle rechts letzte={letzte}>
                            <Kennung>{l.offen ? 'NOCH OFFEN' : 'BEENDET'}</Kennung>
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
                <p className="pt-3 text-xs text-tinte-3">
                  Eine Störung, die tagelang „läuft", ist fast immer ein vergessener Beenden-Klick.
                  Seit August lassen sich echte vergangene Störungen an der Station nachtragen
                  (von–bis) — damit gibt es keinen Grund mehr, eine Meldung offen zu lassen.
                </p>
              </>
            )}
          </Karte>

          <p className="border-t border-linie pt-4 text-xs text-tinte-3">
            Teststörungen zählen nirgends mit.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
